import type { MemberSummary } from './infoserviceApi.js'

/**
 * Textes du quota des adhérents (leur droit de semaines par année
 * d'adhésion). Le calcul est fait par le serveur
 * (`lib/infoservice-member-quota.ts`) : l'écran ne fait que le dire.
 */

/** « 14/03/2027 » : jour civil, aucun fuseau. */
export function frenchDate(day: string | null): string {
  if (!day) return ''
  const [year, month, date] = day.split('-')
  return `${date}/${month}/${year}`
}

export function weeksLabel(count: number): string {
  return `${count} semaine${count > 1 ? 's' : ''}`
}

export type QuotaTone = 'ok' | 'full' | 'over' | 'unknown'

/** `full` à partir du quota atteint ; `over` au-delà ; `unknown` sans date d'adhésion. */
export function quotaTone(member: Pick<MemberSummary, 'period' | 'weeksUsed' | 'quota'>): QuotaTone {
  if (!member.period) return 'unknown'
  if (member.weeksUsed > member.quota) return 'over'
  if (member.weeksUsed === member.quota) return 'full'
  return 'ok'
}

/** Phrase courte sous le choix de l'adhérent, dans la fiche d'annonce. */
export function memberQuotaText(member: MemberSummary): string {
  if (!member.period) return `${weeksLabel(member.weeksUsed)} d’annonces · date d’adhésion à renseigner`
  const parts = [`${member.weeksUsed} semaine${member.weeksUsed > 1 ? 's' : ''} sur ${member.quota}`]
  if (member.weeksOver > 0) parts.push(`${member.weeksOver} de trop`)
  parts.push(member.expired ? `adhésion échue le ${frenchDate(member.period.end)}` : `adhésion jusqu’au ${frenchDate(member.period.end)}`)
  return parts.join(' · ')
}

/** Catégories du fichier du club. */
export const MEMBER_CATEGORIES: Record<number, string> = {
  1: 'Association catégorie 1',
  2: 'Association catégorie 2',
  3: 'Collectivité de moins de 1 500 habitants',
  4: 'Particulier',
  5: 'Entreprise',
  6: 'Collectivité de 1 500 à 5 000 habitants',
  7: 'Collectivité de plus de 5 000 habitants',
  8: 'Infoservice',
  9: 'Entreprise jeune pousse',
}

/** « C4 · Particulier ». */
export function categoryLabel(category: number | null): string {
  return category && MEMBER_CATEGORIES[category] ? `C${category} · ${MEMBER_CATEGORIES[category]}` : 'Sans catégorie'
}

/** Droit annuel par défaut d'une catégorie (même règle que l'import). */
export function quotaForCategory(category: number | null): number {
  if (category === 4) return 6
  if (category === 5 || category === 9) return 52
  return 12
}

/** Échéance : le lendemain du dernier jour de l'année d'adhésion. */
export function renewalDay(period: { end: string } | null): string | null {
  if (!period) return null
  const date = new Date(`${period.end}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + 1)
  return date.toISOString().slice(0, 10)
}

/** « 130,00 € HT », ou le montant tel qu'écrit (« gratuit », « LMM »). */
export function feeLabel(fee: string | null): string | null {
  if (!fee) return null
  return /^\d+(,\d+)?$/.test(fee) ? `${fee} € HT` : fee
}

/** Années de la première adhésion à `currentYear`, avec celles où il ne l'était pas. */
export function membershipYears(years: readonly number[], currentYear: number): { years: Array<{ year: number; member: boolean }>; summary: string } {
  if (years.length === 0) return { years: [], summary: 'Aucune année d’adhésion notée.' }
  const owned = new Set(years)
  const first = Math.min(...years)
  const last = Math.max(currentYear, ...years)
  const all = Array.from({ length: last - first + 1 }, (_, index) => ({ year: first + index, member: owned.has(first + index) }))
  const gaps = all.filter(entry => !entry.member).map(entry => entry.year)
  const count = `${owned.size} année${owned.size > 1 ? 's' : ''} d’adhésion depuis ${first}`
  const tail = gaps.length === 0 ? 'sans interruption'
    : gaps.length <= 3 ? `sans adhésion en ${gaps.join(', ')}`
      : `${gaps.length} années sans adhésion`
  return { years: all, summary: `${count} · ${tail}` }
}

export type QuotaSlot = 'passed' | 'planned' | 'free'

/** Au-delà, la jauge devient une barre : 52 cases ne se lisent plus. */
export const QUOTA_SLOTS_MAX = 12

/** Cases de dépassement dessinées au plus ; le compte (« 23 / 12 ») dit le reste. */
export const QUOTA_OVER_SLOTS_MAX = 3

/**
 * Cases de la jauge : une par semaine du quota, passées puis prévues puis
 * libres ; les semaines au-delà du quota s'ajoutent en bout (`over`), trois
 * au plus pour que la jauge tienne sur sa ligne.
 */
export function quotaSlots(member: Pick<MemberSummary, 'weeksUsed' | 'weeksPassed' | 'quota'>): Array<{ kind: QuotaSlot; over: boolean }> {
  const total = Math.max(member.quota, Math.min(member.weeksUsed, member.quota + QUOTA_OVER_SLOTS_MAX))
  return Array.from({ length: total }, (_, index) => ({
    kind: index < member.weeksPassed ? 'passed' : index < member.weeksUsed ? 'planned' : 'free',
    over: index >= member.quota,
  }))
}

/** « 1 h 02 min », « 12 min 05 s », « 45 s ». */
export function formatAirtime(ms: number): string {
  const total = Math.round(ms / 1000)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  if (hours > 0) return `${hours} h ${String(minutes).padStart(2, '0')} min`
  if (minutes > 0) return `${minutes} min ${String(seconds).padStart(2, '0')} s`
  return `${seconds} s`
}
